import React, { useState } from "react";
import { PageHero, Section, SectionTitle, Reveal, Eyebrow } from "@/components/Editorial";
import {
  TextField,
  SelectField,
  FileField,
  ConsentField,
  SubmitButton,
  SuccessNote,
  useEnquirySubmit,
} from "@/components/EnquiryFields";

const GLOBAL_POINTS = [
  { title: "Market entry assessment", body: "A grounded read of category fit, channel opportunity and regulatory pathway for your brand in India." },
  { title: "Import & regulatory handling", body: "Customs-bonded storage, duty structures and state-level documentation handled by an experienced team." },
  { title: "Portfolio representation", body: "Restrained, professional positioning within a curated portfolio — never volume-first placement." },
  { title: "Channel activation", body: "Access to hospitality, retail, diplomatic and institutional relationships developed since 2001." },
];

const TRADE_POINTS = [
  { title: "Reliable supply", body: "Consistent availability across a curated imported portfolio, supported by disciplined logistics." },
  { title: "Structured logistics", body: "Direct company-fleet delivery across Delhi NCR and structured supply for other markets." },
  { title: "Portfolio access", body: "Wines, beers and spirits from respected producers across Europe and the Americas." },
];

const PATHWAYS = [
  { value: "principal", label: "Brand Principal / Producer", note: "For global beverage houses seeking representation in India." },
  { value: "buyer", label: "Indian Trade Buyer", note: "For hospitality, retail and institutional partners seeking supply." },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "Select portfolio category", disabled: true },
  { value: "Wines", label: "Wines" },
  { value: "Beers", label: "Beers" },
  { value: "Spirits", label: "Spirits" },
  { value: "Non-Alcoholic", label: "Non-Alcoholic / FMCG" },
  { value: "Multiple", label: "Multiple categories" },
];

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  category: "",
  current_markets: "",
  portfolio_size: "",
  distribution_requirement: "",
  message: "",
};

const PartnerForm = () => {
  const [pathway, setPathway] = useState("principal");
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const { status, error, submit } = useEnquirySubmit();

  if (status === "success") return <SuccessNote testId="partner-success" />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isPrincipal = pathway === "principal";

  return (
    <div className="max-w-2xl">
      <div className="grid sm:grid-cols-2 gap-4" role="tablist" aria-label="Enquiry pathway">
        {PATHWAYS.map((p) => (
          <button
            key={p.value}
            type="button"
            role="tab"
            aria-selected={pathway === p.value}
            data-testid={`partner-pathway-${p.value}`}
            onClick={() => setPathway(p.value)}
            className={`text-left px-5 py-4 border transition-colors duration-300 ${
              pathway === p.value
                ? "border-[#4B0F1E] bg-[#4B0F1E] text-[#F7F1E7]"
                : "border-[#1c1713]/25 text-[#1c1713] hover:border-[#4B0F1E]"
            }`}
          >
            <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold">{p.label}</span>
            <span className={`block mt-1.5 text-[12px] font-light leading-snug ${pathway === p.value ? "text-[#F7F1E7]/70" : "text-[#1c1713]/55"}`}>
              {p.note}
            </span>
          </button>
        ))}
      </div>

      <form
        data-testid="partner-form"
        className="mt-10 space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          submit({ ...form, type: pathway, consent: consent ? "true" : "false" }, file);
        }}
      >
        <div className="grid sm:grid-cols-2 gap-6">
          <TextField label="Full name" required testId="partner-name-input" value={form.name} onChange={set("name")} />
          <TextField label="Company" testId="partner-company-input" value={form.company} onChange={set("company")} />
          <TextField label="Email" type="email" required testId="partner-email-input" value={form.email} onChange={set("email")} />
          <TextField label="Phone" testId="partner-phone-input" value={form.phone} onChange={set("phone")} />
          <TextField
            label={isPrincipal ? "Country of origin" : "State / Region"}
            testId="partner-country-input"
            value={form.country}
            onChange={set("country")}
          />
          {isPrincipal && (
            <SelectField
              label="Portfolio category"
              testId="partner-category-select"
              options={CATEGORY_OPTIONS}
              value={form.category}
              onChange={set("category")}
            />
          )}
        </div>

        {isPrincipal ? (
          <div className="grid sm:grid-cols-2 gap-6">
            <TextField
              label="Current export markets"
              placeholder="e.g. UK, Singapore, UAE"
              testId="partner-markets-input"
              value={form.current_markets}
              onChange={set("current_markets")}
            />
            <TextField
              label="Estimated portfolio size"
              placeholder="e.g. 12 SKUs"
              testId="partner-portfolio-size-input"
              value={form.portfolio_size}
              onChange={set("portfolio_size")}
            />
          </div>
        ) : (
          <TextField
            label="Distribution requirement"
            placeholder="e.g. On-trade supply for a hotel group in NCR"
            testId="partner-requirement-input"
            value={form.distribution_requirement}
            onChange={set("distribution_requirement")}
          />
        )}

        <TextField
          label={isPrincipal ? "About your brand and objectives in India" : "Message"}
          required
          textarea
          testId="partner-message-input"
          value={form.message}
          onChange={set("message")}
        />

        <FileField
          label={isPrincipal ? "Brand deck / company profile (optional)" : "Company profile (optional)"}
          file={file}
          onChange={setFile}
          testId="partner-file-input"
        />

        <ConsentField checked={consent} onChange={setConsent} testId="partner-consent-checkbox" />
        {status === "error" && (
          <p data-testid="partner-form-error" className="text-[13px] text-[#4B0F1E]">
            {error}
          </p>
        )}
        <SubmitButton sending={status === "sending"} testId="partner-submit-btn">
          Submit Introduction
        </SubmitButton>
      </form>
    </div>
  );
};

export default function PartnerPage() {
  return (
    <div data-testid="partner-page" className="bg-[#F7F1E7]">
      <PageHero
        eyebrow="Partner With Us"
        title="A route to market for distinguished brands."
        lede="For global beverage houses entering India — and for Indian trade partners seeking reliable supply — HCC offers access, infrastructure, relationships and market intelligence developed since 2001."
      />

      <Section tint>
        <SectionTitle eyebrow="For Global Beverage Houses" title="Introduce your brand to India." />
        <div className="grid sm:grid-cols-2 gap-x-14 gap-y-10 max-w-5xl">
          {GLOBAL_POINTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <Eyebrow>{String(i + 1).padStart(2, "0")}</Eyebrow>
              <h3 className="hcc-serif text-xl mt-3 text-[#1c1713]">{p.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-[#1c1713]/65">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionTitle eyebrow="For Indian Trade Partners" title="Structured supply you can plan around." />
        <div className="grid sm:grid-cols-3 gap-x-12 gap-y-10 max-w-5xl">
          {TRADE_POINTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <h3 className="hcc-serif text-xl text-[#1c1713]">{p.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-[#1c1713]/65">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tint>
        <Reveal>
          <Eyebrow>Begin the Conversation</Eyebrow>
          <h2 className="hcc-serif text-3xl font-medium text-[#1c1713] mt-4">Introduce yourself to our trade desk.</h2>
          <p className="mt-4 mb-10 text-sm font-light leading-relaxed text-[#1c1713]/65 max-w-xl">
            Select the pathway that describes you. Every introduction is reviewed individually —
            considered, case by case.
          </p>
          <PartnerForm />
        </Reveal>
      </Section>
    </div>
  );
}
